import { api, type Page } from "@/lib/api/client";
import type {
  Partner,
  PartnerAddress,
  PartnerAddressCreateInput,
  PartnerAddressUpdateInput,
  PartnerContact,
  PartnerContactCreateInput,
  PartnerContactUpdateInput,
  PartnerCreateInput,
  PartnerFilters,
  PartnerIdentifier,
  PartnerIdentifierInput,
  PartnerRole,
  PartnerUpdateInput,
} from "./partners.types";

/**
 * Cari kartlar ve altındaki dört koleksiyon.
 *
 * MAL KABULÜN "KİMDEN"İ, SEVKİYATIN "KİME"Sİ burada duruyor: stok belgeleri, partiler, araçlar,
 * sürücüler ve taşıma işleri hepsi bir iş ortağına bağlanıyor. Yirmi altı uç aylardır hazırdı ve
 * hiçbir ekranı yoktu — yani veri yalnız başka modüllerin üzerinden, dolaylı olarak girilebiliyordu.
 */
export const partnersApi = {
  list(filters: PartnerFilters = {}): Promise<Page<Partner>> {
    return api.page<Partner>("/partners", { params: { ...filters } });
  },

  get(id: string): Promise<Partner> {
    return api.get<Partner>(`/partners/${id}`);
  },

  /**
   * Gövde ÜRETİLEN sözleşmeden (17 Ekim 2026). Elle yazılmış tip `version`'ı bilmiyordu — sunucu
   * güncellemede istiyor — ve `taxCountry`'yi hiç taşımıyordu; ilk düzenleme formu yazılınca
   * görüldü.
   */
  create(input: PartnerCreateInput): Promise<Partner> {
    return api.post<Partner>("/partners", input);
  },

  update(id: string, input: PartnerUpdateInput): Promise<Partner> {
    return api.patch<Partner>(`/partners/${id}`, input);
  },

  /* ------------------------------------------------------------------ roller */

  roles(partnerId: string): Promise<Page<PartnerRole>> {
    return api.page<PartnerRole>(`/partners/${partnerId}/roles`, { params: { limit: 50 } });
  },

  /**
   * Rol EKLENİR, atanmaz: aynı ortak hem müşteri hem tedarikçi olabilir ve çoğu zaman öyledir.
   * `validFrom`/`validTo` taşıdığı için kapanan bir ilişki silinmez, tarihlenir — geçmiş bir
   * sevkiyatın "o gün taşıyıcımızdı" bilgisini silmek, belgeyi açıklanamaz hâle getirirdi.
   */
  addRole(partnerId: string, input: { roleType: string; validFrom?: string }): Promise<PartnerRole> {
    return api.post<PartnerRole>(`/partners/${partnerId}/roles`, input);
  },

  removeRole(partnerId: string, roleId: string): Promise<void> {
    return api.delete<void>(`/partners/${partnerId}/roles/${roleId}`);
  },

  /* ----------------------------------------------------------------- diğerleri */

  addresses(partnerId: string): Promise<Page<PartnerAddress>> {
    return api.page<PartnerAddress>(`/partners/${partnerId}/addresses`, { params: { limit: 50 } });
  },

  /**
   * Adres ve bağı TEK İSTEKTE: sunucu adres satırını ve karta bağını aynı işlemde yazıyor; türün
   * tek varsayılanını da kendisi koruyor (yeni varsayılan, eskisini düşürür).
   */
  addAddress(partnerId: string, input: PartnerAddressCreateInput): Promise<PartnerAddress> {
    return api.post<PartnerAddress>(`/partners/${partnerId}/addresses`, input);
  },

  updateAddress(
    partnerId: string,
    addressId: string,
    input: PartnerAddressUpdateInput,
  ): Promise<PartnerAddress> {
    return api.patch<PartnerAddress>(`/partners/${partnerId}/addresses/${addressId}`, input);
  },

  removeAddress(partnerId: string, addressId: string): Promise<void> {
    return api.delete<void>(`/partners/${partnerId}/addresses/${addressId}`);
  },

  contacts(partnerId: string): Promise<Page<PartnerContact>> {
    return api.page<PartnerContact>(`/partners/${partnerId}/contacts`, { params: { limit: 50 } });
  },

  createContact(partnerId: string, input: PartnerContactCreateInput): Promise<PartnerContact> {
    return api.post<PartnerContact>(`/partners/${partnerId}/contacts`, input);
  },

  updateContact(
    partnerId: string,
    contactId: string,
    input: PartnerContactUpdateInput,
  ): Promise<PartnerContact> {
    return api.patch<PartnerContact>(`/partners/${partnerId}/contacts/${contactId}`, input);
  },

  removeContact(partnerId: string, contactId: string): Promise<void> {
    return api.delete<void>(`/partners/${partnerId}/contacts/${contactId}`);
  },

  identifiers(partnerId: string): Promise<Page<PartnerIdentifier>> {
    return api.page<PartnerIdentifier>(`/partners/${partnerId}/identifiers`, {
      params: { limit: 50 },
    });
  },

  /**
   * KİMLİK NUMARASI EKLE / DÜZELT / KALDIR (S-19, satis-duzeltme 20 Ekim 2026): GLN, MERSİS, KEP…
   * Uçlar hazırdı, ekran yoktu — kart «Kayıtlı kimlik numarası yok» diyor, ekleme düğmesi yoktu.
   * Aynı kartta aynı tür + değer ikinci kez girilemez (benzersiz dizin); biçim (GLN 13, MERSİS 16
   * hane, T.C. kimlik algoritması) sunucuda, aynısı çekmecede de soruluyor.
   */
  createIdentifier(partnerId: string, input: PartnerIdentifierInput): Promise<PartnerIdentifier> {
    return api.post<PartnerIdentifier>(`/partners/${partnerId}/identifiers`, input);
  },

  updateIdentifier(
    partnerId: string,
    identifierId: string,
    input: PartnerIdentifierInput,
  ): Promise<PartnerIdentifier> {
    return api.patch<PartnerIdentifier>(`/partners/${partnerId}/identifiers/${identifierId}`, input);
  },

  removeIdentifier(partnerId: string, identifierId: string): Promise<void> {
    return api.delete<void>(`/partners/${partnerId}/identifiers/${identifierId}`);
  },

  /**
   * KARTI SİL (S-19): yalnız geçmişi olmayan kart — sunucu teklif, sipariş, stok belgesi, liste
   * ataması, filo ve kanal kaydı olan kartı reddediyor (S-14) ve hangisinin engellediğini söylüyor.
   * Geçmişi olan kart pasife alınır.
   */
  remove(id: string, version: number): Promise<void> {
    return api.delete<void>(`/partners/${id}`, { params: { version } });
  },
};
